<template lang="html">
  <div class="cw-orders">
    <section class="section has-text-centered is-bottom-paddingless">
      <cw-segment-control
        class="types-control"
        :segments="['Pending', 'Completed']"
        :selected="typeSelected"
        @update:selected="updateType"
      ></cw-segment-control>

      <div class="field">
        <div class="control has-icons-left">
          <input
            class="input is-underlined is-medium"
            type="text"
            placeholder="Search Box or Lot"
            v-model="searchQuery"
          />
          <span class="icon is-left">
            <i class="las la-2x la-search"></i>
          </span>
        </div>
      </div>
    </section>
    <section class="section">
      <div class="has-text-centered">
        <span v-if="loading" class="icon has-text-primary is-large">
          <i class="las la-2x la-sync fa-spin"></i>
        </span>
      </div>
      <cw-order-list
        :orders="filteredOrders"
        :selectionHandler="orderSelected"
      ></cw-order-list>
      <div
        class="has-text-centered is-padded-large"
        v-if="
          displayOrderType === 'Complete' &&
            searchQuery === '' &&
            !shouldUseLocationSpecificQueries() &&
            (allCompletedCount > 0 || loading)
        "
      >
        <button
          v-if="hasMoreCompleted"
          class="button is-medium is-light is-rounded"
          :class="{ 'is-loading': loadingMore }"
          :disabled="loadingMore"
          @click="loadMoreCompleted"
        >
          Load More
        </button>
        <p v-else class="help has-text-grey-light">
          All completed orders loaded.
        </p>
      </div>
    </section>

    <section class="section">
      <div class="has-text-centered">
        <div v-if="error" class="centerit">
          <div class="wifi-loader is-primary"></div>
        </div>
        <div class="title is-size-5 font-light has-text-grey-light">
          <p v-if="error" class="is-padded-medium">
            There has been a network error!
          </p>
          <p v-else-if="!loading && (!orders || orders.length === 0)">
            No Orders found
          </p>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  DISPATCH_SET_ORDERS_QUERY,
  DISPATCH_SET_ORDERS_FOR_LOCATION_QUERY,
  STAGED_ORDER_WORK_ORDER,
} from '@/graphql/constants';
import authRequired from '@/components/mixins/auth-required';
import autoRefresh from '@/components/mixins/auto-refresh';
import locationAware from '@/components/mixins/location-aware';

import { mapState } from 'vuex';
import {
  OPTIMISTIC_POLL_INTERVAL_MS,
  OPTIMISTIC_MAX_POLL_ATTEMPTS,
  OPTIMISTIC_MAX_AGE_MS,
} from '@/store/modules/orders';

export default {
  name: 'cw-orders',
  // share common functionality with component mixins
  mixins: [authRequired, autoRefresh, locationAware],
  props: {},
  // variables
  data() {
    return {
      error: undefined,
      loadingOrders: 0,
      loadingLocationOrders: 0,
      orders: [],
      ordersForLocation: [],
      searchQuery: '',
      extraOrders: [],
      hasMoreCompleted: false,
      loadingMore: false,
    };
  },
  apollo: {
    orders: {
      query: DISPATCH_SET_ORDERS_QUERY,
      variables() {
        return {
          status: this.displayOrderType,
          range: this.displayOrderType === 'Complete' ? 20 : null,
        };
      },
      skip() {
        return this.shouldUseLocationSpecificQueries();
      },
      result({ data }) {
        this.error = null;
        this.extraOrders = [];
        // Reconcile optimistic orders once real data arrives
        if (data && data.dispatchSets) {
          this.$store.commit(
            'orders/reconcileOptimisticOrders',
            data.dispatchSets
          );
        }
        if (this.displayOrderType === 'Complete') {
          const count =
            data && data.dispatchSets ? data.dispatchSets.length : 0;
          this.hasMoreCompleted = count >= 20;
        } else {
          this.hasMoreCompleted = false;
        }
      },
      error(err) {
        this.error = err;
      },
      update: data => data.dispatchSets,
      loadingKey: 'loadingOrders',
    },
    ordersForLocation: {
      query: DISPATCH_SET_ORDERS_FOR_LOCATION_QUERY,
      variables() {
        return {
          ...this.getLocationVariables(),
          status: this.displayOrderType,
          range: this.displayOrderType === 'Complete' ? 20 : null,
        };
      },
      skip() {
        return !this.shouldUseLocationSpecificQueries();
      },
      result({ data }) {
        this.error = null;
        // Reconcile optimistic orders once real data arrives
        if (data && data.dispatchSetsForLocation) {
          this.$store.commit(
            'orders/reconcileOptimisticOrders',
            data.dispatchSetsForLocation
          );
        }
      },
      error(err) {
        this.error = err;
      },
      update: data => (data && data.dispatchSetsForLocation) || [],
      loadingKey: 'loadingLocationOrders',
    },
  },
  computed: {
    loading() {
      return (
        this.loadingOrders + this.loadingLocationOrders + this.loadingCustomer >
        0
      );
    },
    allCompletedCount() {
      return this.orders.length + this.extraOrders.length;
    },
    ...mapState('orders', ['optimisticOrders']),
    filteredOrders() {
      // Use location-specific orders if available, otherwise use regular orders
      let realOrders = this.shouldUseLocationSpecificQueries()
        ? this.ordersForLocation
        : [...this.orders, ...this.extraOrders];
      // Merge optimistic orders into Pending view only; they should not appear
      // in Completed until the SQS consumer has written them.
      let orders = realOrders;
      if (this.displayOrderType === 'Pending') {
        const now = Date.now();
        const optimistic = this.optimisticOrders
          // Drop anything we were never able to confirm after a generous
          // ceiling rather than trusting the guess forever.
          .filter(o => now - o._optimisticAt < OPTIMISTIC_MAX_AGE_MS)
          .map(o => ({
            ...o,
            // Flag entries we haven't managed to confirm quickly so the UI
            // can say so instead of looking identical to a real order
            // indefinitely.
            _isDelayed: now - o._optimisticAt >= OPTIMISTIC_POLL_INTERVAL_MS,
          }));
        // Dedup by workOrderId: real orders take precedence. Set orders have
        // no workOrderId to key off of here - reconcileOptimisticOrders
        // (store/modules/orders.js) already matched those by project/lot, so
        // anything left without a workOrderId genuinely hasn't been
        // confirmed yet.
        const realIds = new Set(
          realOrders.map(o => o && o.workOrderId).filter(Boolean)
        );
        const filteredOptimistic = optimistic.filter(
          o => !o.workOrderId || !realIds.has(o.workOrderId)
        );
        orders = [...filteredOptimistic, ...realOrders];
      }
      let query = this.searchQuery.toLowerCase();
      if (query.length > 0) {
        orders = orders.filter(order => {
          let type = order.tripType;
          type = (order.dispatchPull && order.dispatchPull.tripType) || type;

          let matchType = type.toLowerCase().indexOf(query) > -1;
          let matchLot =
            order.subProject &&
            order.subProject.name.toLocaleLowerCase().indexOf(query) > -1;
          let matchBoxNumber =
            order.box && order.box.boxNumber.toLowerCase().indexOf(query) > -1;

          let matchWorkOrder =
            order.workOrderId &&
            order.workOrderId.toLowerCase().indexOf(query) > -1;

          return matchType || matchLot || matchBoxNumber || matchWorkOrder;
        });
      }
      return orders;
    },
    typeSelected() {
      switch (this.displayOrderType) {
        case 'Pending':
          return [0];
        case 'Complete':
          return [1];
        default:
          return [1];
      }
    },
    ...mapState('orders', {
      displayOrderType: state => state.displayOrderType,
    }),
  },
  components: {},
  // methods
  watch: {
    userInfo() {
      this.$apollo.queries.orders.refetch();
    },
  },
  mounted() {
    this._orderSubmittedHandler = ({ success }) => {
      if (success && this.displayOrderType === 'Pending') {
        this.startReconcilePolling();
      }
    };
    this.$broadcast().$on('order-submitted', this._orderSubmittedHandler);

    // An explicit refresh tap should always get a fresh look at outstanding
    // optimistic orders. The attempt cap on the background interval only
    // exists to bound unattended polling - it shouldn't stop us from
    // checking when the user deliberately asks us to.
    this._refreshHandler = () => {
      if (this.optimisticOrders.length > 0) {
        this.checkOutstandingOrders();
      }
    };
    this.$broadcast().$on('refresh-apollo', this._refreshHandler);

    // Resume active re-checking if the Pending list already has outstanding
    // optimistic entries - e.g. the user left and came back before they
    // were confirmed. Check immediately rather than waiting a full interval,
    // since we don't know how long it's already been outstanding.
    if (this.optimisticOrders.length > 0) {
      this.startReconcilePolling();
      this.checkOutstandingOrders();
    }
  },
  beforeDestroy() {
    this.stopReconcilePolling();
    if (this._orderSubmittedHandler) {
      this.$broadcast().$off('order-submitted', this._orderSubmittedHandler);
    }
    if (this._refreshHandler) {
      this.$broadcast().$off('refresh-apollo', this._refreshHandler);
    }
  },
  methods: {
    startReconcilePolling() {
      this.stopReconcilePolling();
      this._reconcileAttempts = 0;
      // Spaced out in minutes rather than seconds - see DetailsBox.vue for
      // why (FileMaker's write path is slow and already taxed, so a tight
      // poll loop would add load without resolving any faster).
      this._reconcileInterval = setInterval(() => {
        this._reconcileAttempts += 1;
        if (this._reconcileAttempts > OPTIMISTIC_MAX_POLL_ATTEMPTS) {
          this.stopReconcilePolling();
          return;
        }
        this.checkOutstandingOrders();
      }, OPTIMISTIC_POLL_INTERVAL_MS);
    },
    stopReconcilePolling() {
      if (this._reconcileInterval) {
        clearInterval(this._reconcileInterval);
        this._reconcileInterval = null;
      }
    },
    // Cheap per-order existence probes to resolve the "did my submission
    // even go through" uncertainty as soon as possible, independent of
    // whether the box's DispatchSet has transitioned to a Pending status
    // yet (a separate, slower dispatch-review step). Also refetches the
    // list itself so fully-processed orders get reconciled and drop the
    // placeholder entirely. Uncapped/no-op-safe so it can be called both
    // from the capped background interval and on-demand (mount resume,
    // manual refresh tap).
    async checkOutstandingOrders() {
      if (this.optimisticOrders.length === 0) {
        this.stopReconcilePolling();
        return;
      }
      const unstaged = this.optimisticOrders.filter(
        o => o && o.workOrderId && !o._isStaged
      );
      await Promise.all(
        unstaged.map(o => this.checkStagedStatus(o.workOrderId))
      );

      this.$apollo.queries.orders.refetch();
      if (this.shouldUseLocationSpecificQueries()) {
        this.$apollo.queries.ordersForLocation.refetch();
      }

      // Once every outstanding service order has been confirmed staged,
      // active polling has done its job - stop and let the normal passive
      // channels (navigation, the header refresh button, the 20-minute
      // global refresh) pick up the eventual transition to a real Pending
      // record instead of continuing to poll FileMaker in the background.
      const stillNeedsStagedCheck = this.optimisticOrders.some(
        o => o && o.workOrderId && !o._isStaged
      );
      if (this.optimisticOrders.length === 0 || !stillNeedsStagedCheck) {
        this.stopReconcilePolling();
      }
    },
    checkStagedStatus(workOrderId) {
      return this.$apollo
        .query({
          query: STAGED_ORDER_WORK_ORDER,
          variables: { workOrderId },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          if (data && data.stagedOrder) {
            this.$store.commit('orders/markOptimisticOrderStaged', workOrderId);
          }
        })
        .catch(err => {
          window.console.error('Error checking staged order status', err);
        });
    },
    orderSelected(order) {
      // Guard against clicking an optimistic order that hasn't been written yet
      if (!order.workOrderId) {
        this.$modal.show('alert', {
          title: 'Order Pending',
          message:
            'This order is still being processed. Please wait a moment and refresh.',
          type: 'info',
        });
        return;
      }
      this.$router.push({ path: `/orders/${order.workOrderId}` });
    },
    async loadMoreCompleted() {
      if (this.loadingMore || !this.hasMoreCompleted) return;
      this.loadingMore = true;
      try {
        const result = await this.$apollo.query({
          query: DISPATCH_SET_ORDERS_QUERY,
          variables: {
            status: 'Complete',
            range: 20,
            offset: this.allCompletedCount + 1,
          },
          fetchPolicy: 'network-only',
        });
        const newOrders =
          result.data && result.data.dispatchSets
            ? result.data.dispatchSets
            : [];
        this.extraOrders = [...this.extraOrders, ...newOrders];
        this.hasMoreCompleted = newOrders.length >= 20;
      } catch (err) {
        this.error = err;
      } finally {
        this.loadingMore = false;
      }
    },
    updateType(t) {
      let display = 'Pending';
      switch (t) {
        case 0:
          display = 'Pending';
          break;
        case 1:
          display = 'Complete';
          break;
        default:
          display = 'Pending';
      }
      this.$store.commit('orders/setDisplayOrderType', { display });
      this.extraOrders = [];
      this.hasMoreCompleted = false;
    },
  },
};
</script>

<style lang="stylus" scoped>
.types-control {
  margin-bottom: 1.5em
}
</style>
